import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '@config/firebase';
import type { User, UserRole, SubscriptionTier } from '@/types';

interface AuthContextValue {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  error: Error | null;

  // Methods
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    role: UserRole,
    displayName?: string
  ) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  clearError: () => void;

  // Derived state
  isAuthenticated: boolean;
  isTeacher: boolean;
  isStudent: boolean;
  isAdmin: boolean;
  subscriptionTier: SubscriptionTier;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Subscribe to auth state changes
  useEffect(() => {
    // Debug logging in development
    const isDev = import.meta.env.DEV;
    const log = (msg: string, data?: unknown) => {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.log(`[AuthProvider] ${msg}`, data ?? '');
      }
    };

    log('Initializing auth state listener');

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      log('Auth state changed', { uid: fbUser?.uid, email: fbUser?.email });
      setFirebaseUser(fbUser);

      if (fbUser) {
        try {
          log('Fetching user document from Firestore');
          const userDoc = await getDoc(doc(db, 'users', fbUser.uid));

          if (userDoc.exists()) {
            const userData = userDoc.data() as User;
            log('User document found', { role: userData.role, email: userData.email });
            setUser(userData);
            setError(null);
          } else {
            // User exists in Auth but not Firestore - incomplete signup
            log('User document NOT found in Firestore - creating fallback');
            // Instead of showing error, create a minimal user object from Firebase Auth
            // This allows the app to function while the full profile is created
            const fallbackUser: User = {
              uid: fbUser.uid,
              email: fbUser.email ?? '',
              displayName: fbUser.displayName ?? fbUser.email?.split('@')[0] ?? 'User',
              role: 'teacher', // Default role - should be updated via proper signup
              subscriptionTier: 'free',
              onboardingCompleted: false,
              createdAt: new Date(),
              updatedAt: new Date(),
              lastActiveAt: new Date(),
            };
            setUser(fallbackUser);
            setError(new Error('User profile incomplete. Some features may be limited.'));
          }
        } catch (err) {
          log('Error fetching user document', err);
          const errorMsg = err instanceof Error ? err.message : 'Failed to fetch user profile';
          setError(new Error(errorMsg));

          // Don't set user to null on Firestore errors if we have Firebase Auth
          // This allows basic auth to work even if Firestore is unavailable
          const fallbackUser: User = {
            uid: fbUser.uid,
            email: fbUser.email ?? '',
            displayName: fbUser.displayName ?? fbUser.email?.split('@')[0] ?? 'User',
            role: 'teacher',
            subscriptionTier: 'free',
            onboardingCompleted: false,
            createdAt: new Date(),
            updatedAt: new Date(),
            lastActiveAt: new Date(),
          };
          setUser(fallbackUser);
          log('Using fallback user due to Firestore error', fallbackUser);
        }
      } else {
        log('No authenticated user');
        setUser(null);
        setError(null);
      }

      setLoading(false);
    });

    // Handle auth initialization errors
    auth.authStateReady().then(() => {
      log('Auth state ready');
    }).catch((err) => {
      log('Auth state ready error', err);
      setLoading(false);
      setError(err instanceof Error ? err : new Error('Failed to initialize auth'));
    });

    return unsubscribe;
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to sign in');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      role: UserRole,
      displayName?: string
    ) => {
      setError(null);
      setLoading(true);

      try {
        const { user: fbUser } = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );

        // Create user document in Firestore
        const userEmail = fbUser.email ?? email;
        const userName = displayName ?? userEmail.split('@')[0] ?? 'User';
        const userData: User = {
          uid: fbUser.uid,
          email: userEmail,
          displayName: userName,
          role,
          subscriptionTier: 'free',
          onboardingCompleted: false,
          createdAt: serverTimestamp() as unknown as Date,
          updatedAt: serverTimestamp() as unknown as Date,
          lastActiveAt: serverTimestamp() as unknown as Date,
        };

        // Add role-specific fields
        if (role === 'teacher') {
          userData.teacher = {
            subjects: [],
          };
        } else if (role === 'student') {
          userData.student = {
            enrolledClassIds: [],
          };
        }

        await setDoc(doc(db, 'users', fbUser.uid), userData);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to create account');
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const signOut = useCallback(async () => {
    setError(null);
    try {
      await firebaseSignOut(auth);
      setUser(null);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to sign out');
      setError(error);
      throw error;
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    setError(null);
    try {
      const sendReset = httpsCallable(functions, 'sendCustomPasswordReset');
      await sendReset({ email });
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to send reset email');
      setError(error);
      throw error;
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const updateProfile = useCallback(async (updates: Partial<User>) => {
    if (!user) {
      throw new Error('No user logged in');
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      // Update local user state
      setUser((prev) => (prev ? { ...prev, ...updates } : null));
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update profile');
      setError(error);
      throw error;
    }
  }, [user]);

  const value: AuthContextValue = {
    user,
    firebaseUser,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateProfile,
    clearError,
    isAuthenticated: !!user,
    isTeacher: user?.role === 'teacher',
    isStudent: user?.role === 'student',
    isAdmin: user?.isAdmin === true || user?.role === 'admin',
    subscriptionTier: user?.subscriptionTier ?? 'free',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return context;
}
